import { useState, useEffect } from "react";
import { ArrowLeft, Radio } from "lucide-react";
import NewsFeed from "./NewsFeed";
import WhaleTracker from "./WhaleTracker";
import TradingViewChart from "./TradingViewChart";
import TVGrid from "./TVGrid";
import PredictionsPanel from "./PredictionsPanel";
import CryptoTicker from "./CryptoTicker";

/**
 * Terminal de mercado estilo Bloomberg/Glint:
 * gráfico TradingView + TV financiera en directo + intel feed de noticias +
 * whale tracker (grandes órdenes spot y flujos DeFi).
 * Todo el contenido multimedia carga en el navegador del usuario.
 * Página pública: los datos de mercado no requieren sesión.
 */

function SectionLabel({ children, live }) {
  return (
    <div className="flex items-center gap-2 px-1">
      {live && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />}
      <h2 className="text-[11px] font-bold tracking-[0.15em] text-gray-500 uppercase">{children}</h2>
    </div>
  );
}

function TerminalPage({ onBack }) {
  const [clock, setClock] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      <header className="border-b border-gray-800 bg-[#0d0d0f]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-[1800px] mx-auto px-4 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-colors text-sm"
              data-testid="terminal-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Volver
            </button>
            <img src="/logo.png" alt="TraceMetrics" className="h-9 w-auto" />
            <div>
              <h1 className="text-base font-bold text-white leading-tight">Terminal de Mercado</h1>
              <p className="text-[11px] text-gray-500">Gráficos · TV · Noticias · Whale tracking</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="flex items-center gap-1.5 text-emerald-400 text-xs font-semibold">
              <Radio className="w-3.5 h-3.5 animate-pulse" /> EN VIVO
            </span>
            <span className="text-gray-400 font-mono hidden sm:inline text-xs">
              {clock.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          </div>
        </div>
      </header>

      {/* Cinta de precios: top 10 por market cap */}
      <CryptoTicker variant="static" />

      <main className="max-w-[1800px] mx-auto px-4 py-4 space-y-4">

        {/* Fila 1: gráfico (75%) + noticias (25%), alineados con la rejilla 4x de la TV */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
          <section className="xl:col-span-3 space-y-3">
            <SectionLabel>Gráfico · TradingView</SectionLabel>
            <div
              className="h-[480px] lg:h-[640px] bg-gray-900/40 border border-gray-800 rounded-xl overflow-hidden"
              data-testid="terminal-chart-panel"
            >
              <TradingViewChart symbol="BINANCE:BTCUSDT" interval="60" />
            </div>
          </section>
          <section className="xl:col-span-1 space-y-3 flex flex-col">
            <SectionLabel live>Intel feed · Noticias</SectionLabel>
            <div className="h-[480px] lg:h-[640px] overflow-hidden">
              <NewsFeed />
            </div>
          </section>
        </div>

        {/* Fila 2: TV en directo, un canal al lado del otro */}
        <section className="space-y-3">
          <SectionLabel live>TV en directo</SectionLabel>
          <TVGrid />
        </section>

        {/* Fila 3: whale tracker + market prediction */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <section className="space-y-3">
            <SectionLabel live>Smart feed · Ballenas y flujos</SectionLabel>
            <WhaleTracker />
          </section>
          <section className="space-y-3">
            <SectionLabel live>Market prediction · Polymarket</SectionLabel>
            <PredictionsPanel />
          </section>
        </div>

        <p className="text-center text-gray-600 text-xs mt-2">
          Datos de mercado y emisiones con fines informativos. No constituyen asesoramiento financiero.
        </p>
      </main>
    </div>
  );
}

export default TerminalPage;
